import React from 'react'
import { Link, useLocation } from 'react-router-dom'

import { useIsMobile } from '@/shared/hooks/useIsMobile'
import ThemeToggle from '@/shared/ui/ThemeToggle'
import GlobalSearch from '@/widgets/global-search/GlobalSearch'

import YesFiWordmark from './YesFiWordmark'

/**
 * Logo heights — mobile matches frontend MobileTopBar proportions (~41px bar),
 * desktop fills the 60px top nav with breathing room.
 */
const WORDMARK_HEIGHT = {
  mobile: 38,
  desktop: 48,
} as const

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const isMobile = useIsMobile()

  const wordmarkHeight = isMobile ? WORDMARK_HEIGHT.mobile : WORDMARK_HEIGHT.desktop

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'var(--app-bg)',
        color: 'var(--app-text-primary)',
      }}
    >
      <header
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 200,
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: isMobile && !isHomePage ? undefined : 'center',
          height:
            isMobile && !isHomePage
              ? undefined
              : isMobile
                ? 'var(--app-header-height-mobile)'
                : 'var(--app-header-height)',
          backgroundColor: 'var(--app-header-bg)',
          backdropFilter: 'var(--backdrop-blur-header)',
          WebkitBackdropFilter: 'var(--backdrop-blur-header)',
          borderBottom: '1px solid var(--app-header-divider)',
        }}
      >
        <div
          style={{
            width: '100%',
            display: isMobile ? 'flex' : !isHomePage ? 'grid' : 'flex',
            ...(isMobile
              ? { justifyContent: 'space-between' }
              : !isHomePage
                ? { gridTemplateColumns: '1fr auto 1fr' }
                : { justifyContent: 'space-between' }),
            alignItems: 'center',
            padding: isMobile ? 'var(--space-xxs) var(--space-14)' : '0 var(--space-2xl)',
            flexDirection: isMobile && !isHomePage ? 'column' : 'row',
            gap: isMobile && !isHomePage ? 'var(--space-lg)' : undefined,
          }}
        >
          {isMobile ? (
            isHomePage ? (
              <div
                style={{
                  width: '100%',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto 1fr',
                  alignItems: 'center',
                }}
              >
                <div />
                <Link
                  to="/"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-md)',
                    color: 'var(--app-text-primary)',
                    transition: 'opacity 0.2s ease',
                  }}
                  aria-label="YesFi Explorer home"
                >
                  <YesFiWordmark height={wordmarkHeight} />
                </Link>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ThemeToggle />
                </div>
              </div>
            ) : (
              <>
                <div
                  style={{
                    width: '100%',
                    display: 'grid',
                    gridTemplateColumns: '1fr auto 1fr',
                    alignItems: 'center',
                  }}
                >
                  <div />
                  <Link
                    to="/"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-md)',
                      color: 'var(--app-text-primary)',
                      transition: 'opacity 0.2s ease',
                    }}
                    aria-label="YesFi Explorer home"
                  >
                    <YesFiWordmark height={wordmarkHeight} />
                  </Link>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <ThemeToggle />
                  </div>
                </div>
                <GlobalSearch
                  variant="header"
                  style={{ width: '100%' }}
                  placeholder="Search Address / Hash / Block..."
                />
              </>
            )
          ) : (
            <>
              <Link
                to="/"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-md)',
                  color: 'var(--app-text-primary)',
                  textDecoration: 'none',
                }}
                aria-label="YesFi Explorer home"
              >
                <YesFiWordmark height={wordmarkHeight} />
              </Link>

              {!isHomePage && (
                <GlobalSearch
                  variant="header"
                  style={{ width: isMobile ? '100%' : 'var(--search-header-width)' }}
                  placeholder="Search Address / Hash / Block..."
                />
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <ThemeToggle />
              </div>
            </>
          )}
        </div>
      </header>
      <main
        style={{
          flex: 1,
          maxWidth: 'var(--content-max-width)',
          width: '100%',
          margin: '0 auto',
          padding: isMobile ? 'var(--space-lg) var(--space-10)' : 'var(--space-2xl) var(--space-3xl)',
        }}
      >
        {children}
      </main>
    </div>
  )
}

export default Layout
